/**
 * Date/time formatting — ported from `AppLocale` in the Flutter app.
 *
 * Unlike currency, dates DO follow the UI language: a Korean cashier expects
 * `2026. 07. 29.`, a Thai one expects the Buddhist-era year. So every function
 * takes the active locale explicitly rather than reading a global.
 */

const cache = new Map<string, Intl.DateTimeFormat>()

function formatter(locale: string, key: string, options: Intl.DateTimeFormatOptions) {
  const cacheKey = `${locale}:${key}`
  const cached = cache.get(cacheKey)
  if (cached) return cached

  const created = new Intl.DateTimeFormat(locale, options)
  cache.set(cacheKey, created)
  return created
}

export const formatDate = (date: Date, locale: string): string =>
  formatter(locale, 'date', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date)

export const formatTime = (date: Date, locale: string): string =>
  formatter(locale, 'time', { hour: '2-digit', minute: '2-digit' }).format(date)

export const formatShortDateTime = (date: Date, locale: string): string =>
  formatter(locale, 'short', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)

/** Long form for the receipt header — includes seconds so two orders in the same minute differ. */
export const formatReceiptDateTime = (date: Date, locale: string): string =>
  formatter(locale, 'receipt', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date)

/** Same calendar day on the terminal's own clock — no UTC, no store timezone. */
const isSameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

/**
 * `HH:mm` for a moment that falls on the same day as `nowMs`, otherwise the full
 * `formatShortDateTime` — the `DD/MM HH:mm` the detail header already gives the
 * placed time.
 *
 * A promised delivery time can be tomorrow: the channel parsers accept
 * `Ngày mai`. A bare `09:00` on a card read at 18:00 tonight reads as a deadline
 * already missed by nine hours, which is the opposite of what it means.
 *
 * "Today" is the terminal's clock and timezone, taken from the same `nowMs`
 * ticker as the elapsed figure this sits beside — the two must never disagree
 * about where the day boundary is, and the till is the only clock the store has.
 */
export const formatTimeWithDateIfNotToday = (date: Date, nowMs: number, locale: string): string =>
  isSameLocalDay(date, new Date(nowMs))
    ? formatTime(date, locale)
    : formatShortDateTime(date, locale)

/**
 * The business day as the reporting endpoints spell it — `YYYY-MM-DD`, built
 * from the terminal's *local* calendar fields.
 *
 * Deliberately not `toISOString().slice(0, 10)`: that is UTC, and a store in
 * `Asia/Ho_Chi_Minh` (UTC+7) asking for "today" at 00:30 would be handed
 * yesterday's numbers with nothing on screen to explain the jump. The
 * dashboard's date arithmetic is calendar arithmetic, so it runs on the
 * calendar the cashier is standing in.
 *
 * The *server* still owns which orders fall in that day — it applies the
 * store's timezone to the boundary. This function only names the day.
 */
export const toBusinessDay = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** `YYYY-MM-DD` back to a local-midnight `Date`, the inverse of `toBusinessDay`. */
export const businessDayToDate = (day: string): Date => {
  const [year, month, date] = day.split('-').map(Number)
  // Noon, not midnight: a DST shift that moves local midnight backwards would
  // land the date on the previous day. Vietnam has no DST, but three locales
  // ship in this app and the terminal's clock is whatever Windows was set to.
  return new Date(year ?? 1970, (month ?? 1) - 1, date ?? 1, 12)
}

/**
 * `delta` days from a business day, staying on the calendar.
 *
 * `Date.setDate` past the end of a month rolls over correctly, which is the
 * whole reason this is not `day ± 86_400_000` on a timestamp.
 */
export const shiftBusinessDay = (day: string, delta: number): string => {
  const date = businessDayToDate(day)
  date.setDate(date.getDate() + delta)
  return toBusinessDay(date)
}

/** Whether a string is a well-formed `YYYY-MM-DD` naming a real calendar day. */
export const isBusinessDay = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false
  // `2026-02-31` matches the pattern and is not a day. Round-tripping catches it:
  // `Date` rolls it over to 2026-03-03, which no longer prints as the input.
  return toBusinessDay(businessDayToDate(value)) === value
}
